import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import Notification from 'utils/Notifications'
import {
  PageHeader,
  Skeleton,
  Card,
} from 'components'
import {
  getAssignments,
  getPublishedAssignments,
  getMySubmissions,
  getSubmissionsForReview,
  getSubmissions,
  createSubmissionDraft,
  updateSubmissionDraft,
  submitSubmission,
  gradeSubmission,
  returnSubmission,
} from 'service/AssignmentsService'

import AssignmentsHeader from './components/AssignmentsHeader/AssignmentsHeader'
import {
  AvailableTable,
  MySubmissionsTable,
  MyTasksTable,
  ReviewQueueTable,
} from './components/AssignmentsTable/AssignmentsTable'
import StudentDrawer from './components/StudentDrawer/StudentDrawer'
import TeacherDrawer from './components/TeacherDrawer/TeacherDrawer'
import styles from './AssignmentsPage.module.scss'

const AssignmentsPage = () => {
  const { t } = useTranslation('common')
  const [me, setMe] = useState(null)
  const [profile, setProfile] = useState(null)
  const [tab, setTab] = useState('available')
  const [available, setAvailable] = useState([])
  const [mySubs, setMySubs] = useState([])
  const [myAssignments, setMyAssignments] = useState([])
  const [reviewQueue, setReviewQueue] = useState([])
  const [loading, setLoading] = useState(true)

  const [drawer, setDrawer] = useState(null)
  const [draftText, setDraftText] = useState('')
  const [draftFiles, setDraftFiles] = useState([])
  const [gradeVal, setGradeVal] = useState('')
  const [gradeComment, setGradeComment] = useState('')

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isStudent = useMemo(() => {
    if (!me) return true
    const roles = me.roles || []
    return roles.includes('student') || me.primary_role === 'student'
  }, [me])
  const isTeacher = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('teacher') || me.primary_role === 'teacher' || roles.includes('lecturer')
  }, [me])

  const loadMe = async () => {
    try {
      const [m, p] = await Promise.allSettled([
        $api.get('/admin-panel/me/'),
        $api.get('/students/me/'),
      ])
      setMe(m.status === 'fulfilled' ? m.value.data : null)
      setProfile(p.status === 'fulfilled' ? p.value.data : null)
    } catch {

    }
  }

  const loadStudent = async () => {
    if (!profile?.id) return
    try {
      const [a, s] = await Promise.allSettled([
        getPublishedAssignments(),
        getMySubmissions(profile.id),
      ])
      setAvailable(a.status === 'fulfilled' ? (a.value.data?.results || a.value.data || []) : [])
      setMySubs(s.status === 'fulfilled' ? (s.value.data?.results || s.value.data || []) : [])
    } catch {

    }
  }

  const loadTeacher = async () => {
    try {
      const [a, r] = await Promise.allSettled([
        getAssignments(),
        getSubmissionsForReview(),
      ])
      setMyAssignments(a.status === 'fulfilled' ? (a.value.data?.results || a.value.data || []) : [])
      setReviewQueue(r.status === 'fulfilled' ? (r.value.data?.results || r.value.data || []) : [])
    } catch {

    }
  }

  const loadAll = async () => {
    setLoading(true)
    await loadMe()
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
  }, [])

  useEffect(() => {
    if (isStudent) loadStudent()
    if (isTeacher) loadTeacher()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, isTeacher, profile?.id])

  const openStudentDrawer = async (a) => {
    let existing = mySubs.find((s) => (s.assignment?.id || s.assignment) === a.id)
    if (!existing && profile?.id) {
      try {
        const r = await getSubmissions({ assignment: a.id, student: profile.id })
        const list = r.data?.results || r.data || []
        if (list.length) existing = list[0]
      } catch {

      }
    }
    setDrawer({ type: 'student', assignment: a, submission: existing || null })
    setDraftText(existing?.content || existing?.text || '')
    setDraftFiles([])
  }

  const openMySubmission = (s) => {
    setDrawer({
      type: 'student',
      submission: s,
      assignment: s.assignment_obj || { id: s.assignment, title: s.assignment_title },
    })
    setDraftText(s?.content || s?.text || '')
    setDraftFiles([])
  }

  const openTeacherDrawer = (sub) => {
    setDrawer({ type: 'teacher', submission: sub, assignment: sub.assignment_obj || sub.assignment })
    setGradeVal(sub.grade ?? '')
    setGradeComment(sub.comment ?? '')
  }

  const closeDrawer = () => {
    setDrawer(null)
    setDraftText('')
    setDraftFiles([])
    setGradeVal('')
    setGradeComment('')
  }

  const saveDraft = async () => {
    if (!drawer || drawer.type !== 'student') return
    try {
      const fd = new FormData()
      if (!drawer.submission) fd.append('assignment', drawer.assignment.id)
      fd.append('content', draftText)
      ;(draftFiles || []).forEach((f) => fd.append('files', f))
      let res
      if (drawer.submission?.id) {
        res = await updateSubmissionDraft(drawer.submission.id, fd)
      } else {
        res = await createSubmissionDraft(fd)
      }
      setNotify({ isOpen: true, message: t('assignments.draft_saved'), type: 'success', sound: 'success' })
      setDrawer({ ...drawer, submission: res.data })
      loadStudent()
    } catch {
      setNotify({ isOpen: true, message: t('assignments.draft_save_failed'), type: 'error', sound: 'error' })
    }
  }

  const sendForReview = async () => {
    if (!drawer?.submission?.id) {
      await saveDraft()
    }
    if (!drawer?.submission?.id) return
    try {
      await submitSubmission(drawer.submission.id)
      setNotify({ isOpen: true, message: t('assignments.sent_for_review'), type: 'success', sound: 'success' })
      closeDrawer()
      loadStudent()
    } catch {
      setNotify({ isOpen: true, message: t('assignments.send_error'), type: 'error', sound: 'error' })
    }
  }

  const accept = async () => {
    if (!drawer?.submission?.id) return
    try {
      await gradeSubmission(drawer.submission.id, { grade: gradeVal, comment: gradeComment })
      setNotify({ isOpen: true, message: t('assignments.graded_ok'), type: 'success', sound: 'success' })
      closeDrawer()
      loadTeacher()
    } catch {
      setNotify({ isOpen: true, message: t('assignments.error'), type: 'error', sound: 'error' })
    }
  }

  const returnWork = async () => {
    if (!drawer?.submission?.id) return
    try {
      await returnSubmission(drawer.submission.id, { comment: gradeComment })
      setNotify({ isOpen: true, message: t('assignments.returned_ok'), type: 'success', sound: 'success' })
      closeDrawer()
      loadTeacher()
    } catch {
      setNotify({ isOpen: true, message: t('assignments.error'), type: 'error', sound: 'error' })
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton variant="card" />
        <Skeleton variant="table" rows={6} cols={5} />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('assignments.title', 'Задания')}
        subtitle={t('assignments.subtitle', 'Список заданий, сдач и проверок')}
      />

      <AssignmentsHeader
        tab={tab}
        setTab={setTab}
        isStudent={isStudent}
        isTeacher={isTeacher}
      />

      <Card className={styles.content}>
        {tab === 'available' && <AvailableTable items={available} onOpen={openStudentDrawer} />}
        {tab === 'mine' && <MySubmissionsTable items={mySubs} onOpen={openMySubmission} />}
        {tab === 'my-tasks' && <MyTasksTable items={myAssignments} />}
        {tab === 'review' && <ReviewQueueTable items={reviewQueue} onOpen={openTeacherDrawer} />}
      </Card>

      {drawer?.type === 'student' && (
        <StudentDrawer
          drawer={drawer}
          draftText={draftText}
          setDraftText={setDraftText}
          setDraftFiles={setDraftFiles}
          onClose={closeDrawer}
          onSaveDraft={saveDraft}
          onSendForReview={sendForReview}
        />
      )}

      {drawer?.type === 'teacher' && drawer.submission && (
        <TeacherDrawer
          drawer={drawer}
          gradeVal={gradeVal}
          setGradeVal={setGradeVal}
          gradeComment={gradeComment}
          setGradeComment={setGradeComment}
          onClose={closeDrawer}
          onAccept={accept}
          onReturn={returnWork}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default AssignmentsPage
